import {
  AppointmentStatus,
  ClientBookingStatus,
  TrackMode,
} from '@fitness-force/enum';
import type { Appointment } from '../schedule/appointment/appointmentlistresponse';

/** Nested class on V2 booking list/detail (includes late-cancel minutes for window calculation). */
export interface ClassBookingV2Class {
  id: number;
  type_id: number;
  company_id: number;
  tenant_id: number;
  type_name_primary_language: string | null;
  type_name_secondary_language: string | null;
  start_datetime: string;
  end_datetime: string;
  tenant_name_primary_language: string | null;
  tenant_name_secondary_language: string | null;
  recurring_id: number;
  type_color: string | null;
  subtype_name_primary_language: string | null;
  subtype_name_secondary_language: string | null;
  max_capacity: number;
  max_waitlist: number;
  behaviour: string;
  is_dropin_allowed: boolean;
  is_virtual: boolean;
  booking_opens_in_minutes: number;
  booking_closes_in_minutes?: number;
  booking_waitlist_closes_in_minutes?: number;
  booking_late_cancel_in_minutes?: number;
  tag_name_primary_language: string[] | null;
  tag_name_secondary_language: string[] | null;
  coach_id: number;
  coach_name: string | null;
  coach_image_url: string | null;
  total_booking: number;
  total_waitlist_booking: number;
  place_id: string | null;
  latitude: number | null;
  longitude: number | null;
  class_duration: number;
  room_id: number;
  guid: string | null;
  status: string;
  accepts_trial: boolean;
  created_by: number;
  created_date: string;
  updated_by: number;
  updated_date: string;
  dropin_plan_id?: number;
}

export interface ClientBookingV2Membership {
  id: number;
  subscription_id: number;
  subscription_primary_name: string | null;
  subscription_secondary_name: string | null;
  subscription_status: string;
  subscription_category: string;
  subscription_end_date: string;
  service_category_primary_name: string | null;
  service_category_secondary_name: string | null;
  membership_name_pri_lang: string | null;
  membership_name_sec_lang: string | null;
  start_date: string;
  end_date: string;
  number_of_sessions: number;
  used_sessions: number;
  available_sessions: number;
  is_selected: boolean;
}

export interface ClassBookingV2 {
  booking_id: number;
  subscription_id: number;
  booking_status: ClientBookingStatus;
  class_booking_is_waitlist: boolean;
  class_booking_is_payment_required: boolean;
  class_booking_is_first_time_class: boolean;
  class_booking_is_comp_session: boolean;
  class_booking_cp_booking: boolean;
  created_track_mode: TrackMode;
  status_updated_track_mode: TrackMode;
  trial_id: number;
  booking_created_date: string;
  is_late_cancel: boolean;
  class: ClassBookingV2Class;
}

export interface ClassBookingV2Detail extends ClassBookingV2 {
  medical_alert: string | null;
  memberships: ClientBookingV2Membership[] | null;
}

export interface AppointmentBookingV2 {
  appointment_id: number;
  status: AppointmentStatus;
  created_track_mode: TrackMode;
  status_updated_track_mode: TrackMode;
  is_first_time_appointment: boolean;
  is_comp_session: boolean;
  is_late_cancel: boolean;
  subscription_id: number;
  membership_id: number;
  appointment_created_date: string;
  appointment: Appointment;
}

export interface AppointmentBookingV2Detail extends AppointmentBookingV2 {
  medical_alert: string | null;
  memberships: ClientBookingV2Membership[] | null;
}

export interface PagedClientBookingsV2Response<T> {
  items: T[] | null;
  total_items: number;
  total_pages: number;
  current_page: number;
}

export interface ListClientBookingsV2Params {
  tenant_id: number;
  client_id: number;
  pageNumber: number;
  pageSize: number;
}

export function getEarlyCancelWindowEnd(
  startIso: string,
  lateCancelMinutes: number | null | undefined,
): Date | null {
  if (!startIso || lateCancelMinutes == null || lateCancelMinutes < 0) {
    return null;
  }
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) {
    return null;
  }
  return new Date(start.getTime() - lateCancelMinutes * 60 * 1000);
}
